import React from 'react';
import { motion } from 'framer-motion';
import { Section, Button } from './index';

/**
 * Products section showcasing premium coffee products
 * Features modern card designs with glassmorphism effects
 */
const Products = () => {
  const products = [
    {
      id: 1,
      name: "Premix Coffee Superbest Power",
      price: "22.90",
      pv: "20.00",
      description: "Premium instant coffee blend with enhanced energy boost",
      image: "☕",
      category: "Premium Coffee",
      features: ["Energy Boost", "Premium Blend", "Instant Mix"]
    },
    {
      id: 2,
      name: "White Coffee Durian Moesang King",
      price: "22.90",
      pv: "20.00",
      description: "Unique Malaysian white coffee with exotic durian flavor",
      image: "🥤",
      category: "Specialty Coffee",
      features: ["Durian Flavor", "White Coffee", "Malaysian Style"]
    },
    {
      id: 3,
      name: "Chocolate More Coco",
      price: "21.90",
      pv: "20.00",
      description: "Rich chocolate cocoa blend for the ultimate indulgence",
      image: "🍫",
      category: "Chocolate Blend",
      features: ["Rich Chocolate", "Premium Cocoa", "1kg Pack"]
    },
    {
      id: 4,
      name: "Premix Goat Milk Powder",
      price: "22.90",
      pv: "20.00",
      description: "Nutritious 3-in-1 goat milk powder for health-conscious consumers",
      image: "🥛",
      category: "Health Blend",
      features: ["Goat Milk", "3-in-1 Formula", "Nutritious"]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <Section id="products" className="py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl md:text-6xl font-bold font-display mb-6">
            <span className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent">
              Premium Coffee
            </span>
            <br />
            <span className="text-coffee-800">Collection</span>
          </h2>
          <p className="text-xl text-coffee-600 max-w-3xl mx-auto leading-relaxed">
            Discover our exclusive range of premium coffee blends and specialty drinks, 
            crafted for the ultimate coffee experience
          </p>
        </motion.div>

        {/* Products Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {products.map((product) => (
            <motion.div
              key={product.id}
              variants={itemVariants}
              whileHover={{ 
                y: -10, 
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
              className="group"
            >
              <div className="bg-white/70 backdrop-blur-lg rounded-3xl p-6 shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-500 h-full flex flex-col">
                {/* Product Image/Icon */}
                <div className="text-6xl mb-4 text-center group-hover:scale-110 transition-transform duration-300">
                  {product.image}
                </div>
                
                {/* Category Badge */}
                <div className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 text-sm font-medium mb-4 self-start">
                  {product.category}
                </div>

                {/* Product Info */}
                <div className="flex-grow">
                  <h3 className="text-xl font-bold text-coffee-800 mb-3 group-hover:text-amber-700 transition-colors duration-300">
                    {product.name}
                  </h3>
                  
                  <p className="text-coffee-600 mb-4 text-sm leading-relaxed">
                    {product.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.features.map((feature, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-coffee-100 text-coffee-700 rounded-lg text-xs font-medium"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Price and Action */}
                <div className="mt-auto">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-2xl font-bold text-amber-600">
                        ${product.price}
                      </div>
                      <div className="text-sm text-coffee-500">
                        PV: {product.pv}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-coffee-600">Bonus Points</div>
                      <div className="text-lg font-semibold text-green-600">
                        {product.price}
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    variant="primary"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    Add to Cart
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <Button
            variant="outline"
            size="lg"
            className="bg-white/80 backdrop-blur-sm border-2 border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            View All Products
          </Button>
        </motion.div>
      </div>
    </Section>
  );
};

export default Products;